import React, { ComponentType, useState } from "react";
import MyCasesIcon from "../../../assets/icons/serviceOverview/IconContract";
import ServiceWorldContentBox from "../ServiceWorldContentBox";
import _map from "lodash/map";
import ServiceWorldCaseItem from "./ServiceWorldCaseItem";
import { MyCasesLoader } from "../../ContentLoader/MyCasesLoader";
import { Box, CardActionArea, Collapse, Divider, List, ListItem } from "@mui/material";
import Grid from "@mui/material/Grid";
import { ExpandLess, ExpandMore } from "@mui/icons-material";
import Typography from "@mui/material/Typography";
import serviceWorldMyCasesStyle from "./serviceWorldMyCasesStyle";
import { useQuery } from "@tanstack/react-query";
import { fetchCollection, queryKeys } from "../../../services/reactQuery/reactQueryService";
import { splitUpByCompletedStatus } from "../../../services/serviceWorldService";
import { MinimalCase } from "../../../types/Entities/MinimalCase";

type ServiceWorldMyCaseProps = {
  NotificationComponent?: ComponentType<{ minimalCase: MinimalCase }> | null;
  listItemButtonConfig?: { getLink?: Function; getIsSelected?: Function; handleClick?: Function };
};

const ServiceWorldMyCases = ({ NotificationComponent, listItemButtonConfig }: ServiceWorldMyCaseProps) => {
  const [expandClosedCases, setExpandClosedCases] = useState(false);
  const { data: minimalCases, isLoading } = useQuery({
    queryKey: queryKeys.collection("/collection/cases"),
    queryFn: () => fetchCollection("/collection/cases")
  });

  if (isLoading) {
    return (
      <Box sx={serviceWorldMyCasesStyle.myCasesContainer}>
        <Box sx={serviceWorldMyCasesStyle.scrollToAnchor} id={"my-cases"} />
        <ServiceWorldContentBox headline={"Meine Fälle"} icon={MyCasesIcon}>
          <MyCasesLoader />
        </ServiceWorldContentBox>
      </Box>
    );
  }

  const { cases, closedCases } = splitUpByCompletedStatus(minimalCases);

  const CasesStatus = () => {
    switch (true) {
      case cases.length === 0 && closedCases.length === 0:
        return (
          <>
            <Divider sx={serviceWorldMyCasesStyle.divider} />
            <Box sx={serviceWorldMyCasesStyle.noCase}>Kein Fall vorhanden</Box>
          </>
        );
      case cases.length === 0 && closedCases.length > 0:
        return (
          <>
            <Divider sx={serviceWorldMyCasesStyle.divider} />
            <Box sx={serviceWorldMyCasesStyle.noCase}>Kein aktiver Fall vorhanden</Box>
          </>
        );
      default:
        return null;
    }
  };

  return (
    <Box sx={serviceWorldMyCasesStyle.myCasesContainer}>
      <ServiceWorldContentBox headline={"Meine Fälle"} icon={MyCasesIcon}>
        <CasesStatus />
        {cases.length > 0 && (
          <List disablePadding>
            {_map(cases, (minimalCase) => (
              <React.Fragment key={minimalCase.id}>
                <ListItem disablePadding>
                  <ServiceWorldCaseItem minimalCase={minimalCase} NotificationComponent={NotificationComponent} listItemButtonConfig={listItemButtonConfig} />
                </ListItem>
              </React.Fragment>
            ))}
          </List>
        )}
        {closedCases.length > 0 && (
          <>
            <CardActionArea
              onClick={() => setExpandClosedCases(!expandClosedCases)}
              sx={{
                ...serviceWorldMyCasesStyle.expandClosedCases,
                ...(expandClosedCases ? {} : { borderRadius: "0 0 25px 25px" }),
              }}
            >
              <Grid
                container
                spacing={3}
                sx={{
                  alignItems: "center",
                  justifyContent: "center"
                }}>
                <Grid>
                  <Typography variant={"h6"} sx={serviceWorldMyCasesStyle.expandClosedCasesText}>
                    Abgeschlossene Fälle
                  </Typography>
                </Grid>
                <Grid>{expandClosedCases ? <ExpandLess /> : <ExpandMore />}</Grid>
              </Grid>
            </CardActionArea>
            <Collapse in={expandClosedCases}>
              <List disablePadding>
                {_map(closedCases, (minimalCase, index) => (
                  <React.Fragment key={minimalCase.id}>
                    <ListItem disablePadding>
                      <ServiceWorldCaseItem
                        minimalCase={minimalCase}
                        NotificationComponent={NotificationComponent}
                        listItemButtonConfig={listItemButtonConfig}
                        withDivider={index > 0}
                      />
                    </ListItem>
                  </React.Fragment>
                ))}
              </List>
            </Collapse>
          </>
        )}
      </ServiceWorldContentBox>
    </Box>
  );
};

export default ServiceWorldMyCases;
